import {
    Flex,
    HStack,
    VStack,
    Box,
    IconButton,
    Button,
    Text,
    FormRow,
    Checkbox,
    FormSpacer,
} from '@cad-arc';
import { BoloSearchOptions, EventSearchOptions } from '@mark43/cad-api';
import { useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { FormDateInput } from '../forms/integrated-form-controls/form-date-input';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { SearchPersonsForm } from './types';
import { INPUT_WIDTH_SM } from '../search-windows/constants';
import { useIntegratedForm } from '../forms/use-integrated-form';

export function SearchFormPersonFields({
    options,
    index,
    isFirstResponder,
    isRemovable,
    onRemove,
}: {
    options: EventSearchOptions | BoloSearchOptions;
    index: number;
    isRemovable: boolean;
    onRemove: () => void;
    isFirstResponder?: boolean;
}) {
    const { form } = useIntegratedForm<SearchPersonsForm>();
    const [showAgeRange, setShowAgeRange] = useState<boolean>(false);
    useEffect(
        function clearHiddenFields() {
            if (showAgeRange) {
                form.resetField(`persons.${index}.dateOfBirth`);
            } else {
                form.resetField(`persons.${index}.ageMin`);
                form.resetField(`persons.${index}.ageMax`);
            }
        },
        [showAgeRange, form, index]
    );
    const [showHeightRange, setShowHeightRange] = useState<boolean>(false);
    useEffect(
        function clearHiddenFields() {
            if (showHeightRange) {
                form.resetField(`persons.${index}.height`);
            } else {
                form.resetField(`persons.${index}.heightMin`);
                form.resetField(`persons.${index}.heightMax`);
            }
        },
        [showHeightRange, form, index]
    );
    const [showWeightRange, setShowWeightRange] = useState<boolean>(false);
    useEffect(
        function clearHiddenFields() {
            if (showWeightRange) {
                form.resetField(`persons.${index}.weight`);
            } else {
                form.resetField(`persons.${index}.weightMin`);
                form.resetField(`persons.${index}.weightMax`);
            }
        },
        [showWeightRange, form, index]
    );
    const [showDetails, setShowDetails] = useState<boolean>(false);
    useEffect(
        function clearHiddenFields() {
            if (!showDetails) {
                form.resetField(`persons.${index}.race`);
                form.resetField(`persons.${index}.sex`);
                form.resetField(`persons.${index}.height`);
                form.resetField(`persons.${index}.heightMin`);
                form.resetField(`persons.${index}.heightMax`);
                form.resetField(`persons.${index}.weight`);
                form.resetField(`persons.${index}.weightMin`);
                form.resetField(`persons.${index}.weightMax`);
                form.resetField(`persons.${index}.eyeColor`);
                form.resetField(`persons.${index}.hairColor`);
            }
        },
        [showDetails, form, index]
    );
    const showInvolvementTypeOther = useWatch({
        control: form.control,
        name: `persons.${index}.involvementType`,
    })?.isOther;
    useEffect(
        function resetInvolvementTypeOther() {
            if (!showInvolvementTypeOther) {
                form.resetField(`persons.${index}.involvementTypeOther`);
            }
        },
        [form, index, showInvolvementTypeOther]
    );
    return (
        <VStack align="left">
            {isRemovable && (
                <HStack justifyContent="space-between">
                    <Text variant="headingXs">Person {index + 1}</Text>
                    <IconButton
                        variant="ghost"
                        icon="TrashCan"
                        aria-label="remove person"
                        size={isFirstResponder ? 'lg' : 'sm'}
                        onClick={() => onRemove()}
                    />
                </HStack>
            )}
            <>
                <Flex flex={1} gap="1rem" flexFlow="wrap">
                    <Box width={INPUT_WIDTH_SM}>
                        <FormInput
                            name={`persons.${index}.firstName`}
                            label="First Name"
                            control={form.control}
                        />
                    </Box>
                    <Box width={INPUT_WIDTH_SM}>
                        <FormInput
                            name={`persons.${index}.lastName`}
                            label="Last Name"
                            control={form.control}
                        />
                    </Box>
                    <Box width="5rem">
                        <FormInput
                            name={`persons.${index}.middleName`}
                            label="M.I."
                            control={form.control}
                        />
                    </Box>
                </Flex>
                <Flex flex={1} gap="1rem" flexFlow="wrap">
                    <Box width={INPUT_WIDTH_SM}>
                        <FormInput
                            name={`persons.${index}.phoneNumber`}
                            label="Phone"
                            control={form.control}
                        />
                    </Box>
                    <Box width="5rem">
                        <FormInput
                            name={`persons.${index}.phoneExtension`}
                            label="Ext."
                            control={form.control}
                        />
                    </Box>
                    {showAgeRange ? (
                        <>
                            <Box width="4rem">
                                <FormNumberInput
                                    name={`persons.${index}.ageMin`}
                                    label="Min Age"
                                    control={form.control}
                                    numberInputProps={{ precision: 0 }}
                                />
                            </Box>
                            <Box width="4rem">
                                <FormNumberInput
                                    name={`persons.${index}.ageMax`}
                                    label="Max Age"
                                    control={form.control}
                                    numberInputProps={{ precision: 0 }}
                                    rules={{
                                        validate: (ageMax) => {
                                            const ageMin = form.getValues(
                                                `persons.${index}.ageMin`
                                            );
                                            if (ageMin) {
                                                if (ageMax <= ageMin) {
                                                    return 'Must be greater than min';
                                                }
                                            }
                                        },
                                    }}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box width="9rem">
                            <FormDateInput
                                name={`persons.${index}.dateOfBirth`}
                                label="Date of Birth"
                                control={form.control}
                            />
                        </Box>
                    )}
                    <Box alignSelf="flex-end">
                        <FormSpacer alignItems="center">
                            <Checkbox
                                data-testid="show-age-range"
                                isChecked={showAgeRange}
                                onChange={() => setShowAgeRange(!showAgeRange)}
                            >
                                Unknown
                            </Checkbox>
                        </FormSpacer>
                    </Box>
                </Flex>
                <Flex flex={1} gap="1rem" flexFlow="wrap">
                    <FormInput
                        name={`persons.${index}.driversLicenseNumber`}
                        label="DL #"
                        control={form.control}
                        length="sm"
                    />
                    <FormSelect
                        name={`persons.${index}.driversLicenseState`}
                        label="DL State"
                        control={form.control}
                        options={options.stateOptions}
                        length="sm"
                    />
                    <FormInput
                        name={`persons.${index}.socialSecurityNumber`}
                        label="SSN"
                        control={form.control}
                        length="sm"
                    />
                </Flex>
                <Flex flex={1} gap="1rem" flexFlow="wrap">
                    <Box width="16rem">
                        <FormInput
                            name={`persons.${index}.notes`}
                            label="Notes"
                            control={form.control}
                        />
                    </Box>
                    <Box width={INPUT_WIDTH_SM}>
                        <FormSelect
                            name={`persons.${index}.involvementType`}
                            label="Involvement Type"
                            control={form.control}
                            options={options.personInvolvementTypeOptions}
                        />
                    </Box>

                    {showInvolvementTypeOther && (
                        <Box width={INPUT_WIDTH_SM}>
                            <FormInput
                                name={`persons.${index}.involvementTypeOther`}
                                label="Description"
                                control={form.control}
                            />
                        </Box>
                    )}
                    <Box alignSelf="flex-end">
                        <FormSpacer alignItems="center">
                            <Button
                                id={`show-details-${index}`}
                                variant="ghost"
                                aria-label="hide / show person details"
                                leadingVisual={showDetails ? 'Close' : 'Open'}
                                onClick={() => setShowDetails(!showDetails)}
                            >{`${showDetails ? 'Hide' : 'Show'} Details`}</Button>
                        </FormSpacer>
                    </Box>
                </Flex>
                {showDetails && (
                    <>
                        <FormRow>
                            <FormSelect
                                name={`persons.${index}.race`}
                                label="Race"
                                control={form.control}
                                options={options.raceOptions}
                                length="sm"
                            />
                            <FormSelect
                                name={`persons.${index}.sex`}
                                label="Sex"
                                control={form.control}
                                options={options.sexOptions}
                                length="sm"
                            />
                        </FormRow>
                        <FormRow>
                            {showHeightRange ? (
                                <>
                                    <FormNumberInput
                                        name={`persons.${index}.heightMin`}
                                        label="Min Height"
                                        control={form.control}
                                        length="sm"
                                        numberInputProps={{ precision: 0 }}
                                    />
                                    <FormNumberInput
                                        name={`persons.${index}.heightMax`}
                                        label="Max Height"
                                        control={form.control}
                                        length="sm"
                                        numberInputProps={{ precision: 0 }}
                                    />
                                </>
                            ) : (
                                <FormNumberInput
                                    name={`persons.${index}.height`}
                                    label="Height"
                                    control={form.control}
                                    length="sm"
                                    numberInputProps={{ precision: 0 }}
                                />
                            )}
                            <Box alignSelf="flex-end">
                                <FormSpacer alignItems="center">
                                    <Checkbox
                                        data-testid="show-height-range"
                                        isChecked={showHeightRange}
                                        onChange={() => setShowHeightRange(!showHeightRange)}
                                    >
                                        Unknown
                                    </Checkbox>
                                </FormSpacer>
                            </Box>
                        </FormRow>
                        <FormRow>
                            {showWeightRange ? (
                                <>
                                    <FormNumberInput
                                        name={`persons.${index}.weightMin`}
                                        label="Min Weight"
                                        control={form.control}
                                        length="sm"
                                        numberInputProps={{ precision: 0 }}
                                    />
                                    <FormNumberInput
                                        name={`persons.${index}.weightMax`}
                                        label="Max Weight"
                                        control={form.control}
                                        length="sm"
                                        numberInputProps={{ precision: 0 }}
                                        rules={{
                                            validate: (weightMax) => {
                                                const weightMin = form.getValues(
                                                    `persons.${index}.weightMin`
                                                );
                                                if (weightMin) {
                                                    if (weightMax <= weightMin) {
                                                        return 'Must be greater than min';
                                                    }
                                                }
                                            },
                                        }}
                                    />
                                </>
                            ) : (
                                <FormNumberInput
                                    name={`persons.${index}.weight`}
                                    label="Weight"
                                    control={form.control}
                                    length="sm"
                                />
                            )}
                            <Box alignSelf="flex-end">
                                <FormSpacer alignItems="center">
                                    <Checkbox
                                        data-testid="show-weight-range"
                                        isChecked={showWeightRange}
                                        onChange={() => setShowWeightRange(!showWeightRange)}
                                    >
                                        Unknown
                                    </Checkbox>
                                </FormSpacer>
                            </Box>
                        </FormRow>
                        <FormRow>
                            <FormSelect
                                name={`persons.${index}.eyeColor`}
                                label="Eye Color"
                                control={form.control}
                                options={options.eyeColorOptions}
                                length="sm"
                            />
                            <FormSelect
                                name={`persons.${index}.hairColor`}
                                label="Hair Color"
                                control={form.control}
                                options={options.hairColorOptions}
                                length="sm"
                            />
                        </FormRow>
                    </>
                )}
            </>
        </VStack>
    );
}
